import styled from '@emotion/styled'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import QRCodeElement from './QRCodeElement'
import GrooverText from './GrooverText'
import useIsMobile from '../hooks/useIsMobile'
import { REGISTER_PAGE_MAX_WIDTH } from '../enums/HardCodedValues'
import { RegistrationQrCodeItem } from '../hooks/useRegistrationQrCodes'
import { getGrooverText } from '../libs/GrooverTextRepository'
import {
  QrExpandedCloseButton,
  QrExpandedContent,
  QrExpandedOverlay,
  QrExpandedQrWrapper,
  QrExpandedInstruction,
  QrPaymentPendingBadge
} from './qr/QrExpandedOverlay.styles'

type Props = {
  qrData: RegistrationQrCodeItem
  isPaymentPending?: boolean
}

const RegistrationQrFab = ({ qrData, isPaymentPending = false }: Props) => {
  const isMobile = useIsMobile()
  const [isOpen, setIsOpen] = useState(false)

  const triggerRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  const closeOverlay = useCallback(() => {
    setIsOpen(false)
    // Restore focus to the trigger button when the dialog closes.
    requestAnimationFrame(() => triggerRef.current?.focus())
  }, [])

  // Body scroll lock, Escape key, and focus management.
  useEffect(() => {
    if (!isOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Move focus into the dialog when it opens.
    closeButtonRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeOverlay()
        return
      }

      // Focus trap: keep Tab/Shift+Tab inside the dialog.
      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute('disabled'))
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen, closeOverlay])

  const qrSize = isMobile ? Math.min(Math.round(window.innerWidth * 0.8), 260) : 280

  return (
    <>
      {!isOpen && (
        <FabContainer $isMobile={isMobile}>
          <FabButton ref={triggerRef} type="button" onClick={() => setIsOpen(true)} $isMobile={isMobile}>
            <QrCode2Icon fontSize="small" aria-hidden sx={{ color: 'inherit' }} />
            <GrooverText textKey="registrationQrFabOpenLabel" style={{ color: '#ffffff' }} />
          </FabButton>
        </FabContainer>
      )}

      {isOpen && (
        <QrExpandedOverlay onClick={closeOverlay} role="presentation">
          <QrExpandedContent
            ref={dialogRef}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="registration-qr-fab-title"
          >
            <QrExpandedCloseButton
              ref={closeButtonRef}
              type="button"
              onClick={closeOverlay}
              aria-label={getGrooverText('registrationQrFabCloseLabel')}
            >
              ×
            </QrExpandedCloseButton>
            <QrExpandedInstruction id="registration-qr-fab-title">
              <GrooverText textKey="registrationStep6SummaryQrCodeLabel" />
            </QrExpandedInstruction>
            {qrData.isMultipleTickets && (
              <span style={{ fontSize: 14, color: '#6b7280' }}>
                {getGrooverText('registrationStep6SummaryQrCodeTicketLabel')} {qrData.ticketIndex}
              </span>
            )}
            {isPaymentPending && (
              <QrPaymentPendingBadge>
                <GrooverText textKey="registrationQrFabPaymentPendingLabel" style={{ color: 'inherit' }} />
              </QrPaymentPendingBadge>
            )}
            <QrExpandedQrWrapper>
              <QRCodeElement value={qrData.qrCode} showText={false} size={qrSize} />
            </QrExpandedQrWrapper>
          </QrExpandedContent>
        </QrExpandedOverlay>
      )}
    </>
  )
}

const FabContainer = styled.div<{ $isMobile: boolean }>`
  position: fixed;
  z-index: 50;
  color-scheme: light only;
  isolation: isolate;

  ${({ $isMobile }) =>
    $isMobile
      ? `
    left: 0;
    right: 0;
    bottom: 0;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
    background: rgba(255, 255, 255, 0.98);
    border-top: 1px solid #e0e0e0;
    box-shadow: 0 -6px 16px rgba(0, 0, 0, 0.08);
    display: flex;
    justify-content: center;
  `
      : `
    right: 24px;
    bottom: 24px;
  `}
`

const FabButton = styled.button<{ $isMobile: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  background: #7c3aed;
  color-scheme: light only;
  transition: opacity 0.2s ease;
  max-width: ${REGISTER_PAGE_MAX_WIDTH};
  box-sizing: border-box;

  ${({ $isMobile }) =>
    $isMobile
      ? `
    width: 100%;
    min-height: 48px;
    border-radius: 8px;
    padding: 12px 16px;
  `
      : `
    min-height: 44px;
    padding: 12px 20px;
    border-radius: 999px;
    box-shadow: 0 4px 14px rgba(124, 58, 237, 0.35);
  `}

  &:hover {
    opacity: 0.92;
  }

  &:active {
    opacity: 0.85;
  }

  svg {
    color: inherit;
  }
`

export default memo(RegistrationQrFab)
